import React, { useCallback, useMemo, useRef, useState } from 'react'
import {
  GET_DELIVERY_AREAS,
  PAGE_SIZE_ON_DELIVERY_AREAS,
  useExportExcelOnDeliveryAreas,
  useGetCitiesOnDeliveryAreas,
  useGetDeliveryAreas,
  useUpdateDeliveryArea,
  useUploadExcelOnDeliveryAreas,
} from '@/services/deliveryAreas/useDeliveryAreasQueries'
import { HStack, VStack } from '@musinsa/mwms-ds'
import { FormProvider, useForm } from 'react-hook-form'
import { DeliveryAreaItem, Carrier, GetDeliveryAreasReq, UpdateDeliveryAreaReq } from '@/services/deliveryAreas/types'
import DeliveryAreasForm, { INIT_FORM } from '@/pages/master/deliveryAreas/components/DeliveryAreasForm'
import { AgGridReact } from 'ag-grid-react'
import { flushSync } from 'react-dom'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { ColDef } from 'ag-grid-community'
import MAlert from '@/utils/modal/MAlert'
import MToast from '@/utils/modal/MToast'
import MConfirm from '@/utils/modal/MConfirm'
import { queryClient } from '@/utils/queryClient'
import useModal from '@/hooks/useModal'
import DeliveryAreasAddModal from '@/pages/master/deliveryAreas/components/DeliveryAreasAddModal'
import { Utils } from '@/utils/Utils'
import { omitBy } from 'lodash-es'
import GridTable from '@/components/table/GridTable'
import ExcelDownloadModal from '@/components/table/ExcelDownloadModal'
import { PageLayout } from '@/components/layout/PageLayout'
import TitleSection from '@/components/content/TitleSection'
import { DeliveryAreasTableTitle } from './components/DeliveryAreasTableTitle'
import { useCellChangeValidation } from './hooks/useCellChangeValidation'
import { UploadResultModal } from './components/UploadResultModal'
import MRender from '@/utils/modal/MRender'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'

const Carriers: Carrier[] = ['HJ', 'JA']

function DeliveryAreas() {
  const { reload } = useMenuOnSession()

  const { parsedQs, replaceQuery } = useParsedQuery<GetDeliveryAreasReq>()
  const form = useForm<GetDeliveryAreasReq>({ defaultValues: { ...INIT_FORM, ...parsedQs } })

  const [queryKey, setQueryKey] = useState(form.getValues())
  const [currentPage, setCurrentPage] = useState(0)

  const { data, isFetching, refetch } = useGetDeliveryAreas({ ...queryKey, page: currentPage })
  const updateDeliveryArea = useUpdateDeliveryArea()

  const { onExportData } = useExportExcelOnDeliveryAreas(queryKey)
  const { onUploadData } = useUploadExcelOnDeliveryAreas()
  const { data: cities } = useGetCitiesOnDeliveryAreas()
  const addModal = useModal()
  const exportModal = useModal()

  const $grid = useRef<AgGridReact>(null)

  const { handleCheckTownAndDistrict, handleCheckReadDay, handleCheckRemoteArea } = useCellChangeValidation({
    $grid,
  })

  const columnDefs = useMemo<ColDef<DeliveryAreaItem>[]>(() => {
    return [
      { field: 'zipcode', headerName: '우편번호', width: 110 },
      {
        field: 'city',
        headerName: '시구분',
        editable: true,
        width: 180,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: { values: cities },
      },
      { field: 'district', headerName: '구구분', editable: true, width: 180, valueSetter: handleCheckTownAndDistrict },
      { field: 'town', headerName: '읍면동', editable: true, width: 180, valueSetter: handleCheckTownAndDistrict },
      {
        field: 'deliveryLeadDay',
        headerName: '배송리드타임',
        editable: true,
        width: 130,
        cellDataType: 'number',
        valueSetter: handleCheckReadDay,
      },
      { field: 'remoteArea', headerName: '도서산간여부', editable: true, type: 'checkbox', width: 130, valueSetter: handleCheckRemoteArea },
      { field: 'isHolidayDeliveryAvailable', headerName: '휴일배송여부', editable: true, type: 'checkbox', width: 130 },
      {
        field: 'defaultCarrier',
        headerName: '주중 운송사',
        editable: true,
        width: 130,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: { values: Carriers },
      },
      {
        field: 'holidayCarrier',
        headerName: '휴일 운송사',
        editable: true,
        width: 130,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: { values: Carriers },
      },
      { field: 'createdAt', headerName: '생성일자', cellDataType: 'datetime', width: 150 },
      { field: 'createdBy', headerName: '생성자', width: 200 },
      { field: 'lastModifiedAt', headerName: '수정일자', cellDataType: 'datetime', width: 150 },
      { field: 'lastModifiedBy', headerName: '수정자', width: 200 },
    ]
  }, [cities])

  const handleSubmit = useCallback(async (formData: GetDeliveryAreasReq) => {
    $grid.current?.api?.deselectAll()
    queryClient.resetQueries({ queryKey: GET_DELIVERY_AREAS })

    const query = omitBy(formData, (value) => value === '') as GetDeliveryAreasReq

    MenuStorage.menuOnSession.setSearch(query)
    reload()

    flushSync(() => {
      setQueryKey(query)
      setCurrentPage(0)
    })

    await refetch()
    replaceQuery(query)
  }, [])

  const onChangePage = useCallback((page: number) => {
    $grid.current?.api?.deselectAll()

    flushSync(() => setCurrentPage(page))
    refetch()
  }, [])

  const handleSave = useCallback(() => {
    const selectedRows: UpdateDeliveryAreaReq[] = $grid.current?.api?.getSelectedRows() || []
    if (!selectedRows.length) return MToast.error('저장할 데이터가 선택되지 않았습니다.')

    updateDeliveryArea
      .mutateAsync(selectedRows.map((row) => ({ ...row, district: row.district || null })))
      .then(async () => {
        MToast.show(`${selectedRows.length}개 수정 완료`)

        $grid.current?.api?.deselectAll()
        await refetch()
      })
      .catch((e) => {
        MAlert.show(Utils.getApiErrMessage(e, '저장에 실패했습니다.'))
      })
  }, [])

  const onDeleteRows = useCallback((deleted: DeliveryAreaItem[]) => {
    if (!deleted.length) return MToast.error('삭제할 데이터가 선택되지 않았습니다.')

    updateDeliveryArea
      .mutateAsync(deleted.map((row) => ({ ...row, deleted: true })))
      .then(async () => {
        MToast.show(`${deleted.length}개 삭제 완료`)

        $grid.current?.api?.deselectAll()
        await refetch()
      })
      .catch((e) => {
        MAlert.show(Utils.getApiErrMessage(e, '삭제에 실패했습니다.'))
      })
  }, [])

  const handleUploadData = useCallback(
    (file: File) => {
      MConfirm.show('우편번호 권역에 대해 신규 추가 및 변경 업데이트를 진행합니다.\n진행하시겠습니까?', {
        onConfirm: () => {
          onUploadData(file)
            .then((response) => {
              MRender.show(<UploadResultModal onUpload={handleUploadData} data={response.data} onClose={() => MRender.destory()} />)
              handleSubmit(queryKey)
            })
            .catch((e) => {
              MAlert.show(Utils.getApiErrMessage(e, '업로드에 실패했습니다.'))
            })
        },
      })
    },
    [queryKey],
  )

  const handleExportData = useCallback(() => {
    if (!queryKey.zipcode && !queryKey.city) {
      return MToast.error("데이터 조회 완료 후, 다운로드가 가능합니다. '우편번호' 및 '시구분' 값을 설정하여 조회 후, 다운로드 진행해주세요.")
    }

    // 조회 여부는 사유 모달을 열기 전에 본다 — 사유를 다 적은 뒤 막히지 않게
    exportModal.onOpenModal()
  }, [exportModal, queryKey])

  return (
    <>
      <FormProvider {...form}>
        <PageLayout gap={16}>
          <VStack gap={12}>
            <TitleSection variant="legacy" title="우편번호권역 관리" />
            <DeliveryAreasForm isLoading={isFetching} onSubmit={handleSubmit} />
          </VStack>
          <HStack full gap={16}>
            <GridTable<DeliveryAreaItem>
              gridKey="MA13_HEAD"
              title={
                <DeliveryAreasTableTitle
                  $grid={$grid}
                  onDelete={() => onDeleteRows($grid.current?.api?.getSelectedRows() || [])}
                  onSave={handleSave}
                  onRefetch={refetch}
                />
              }
              rowSelectable
              ref={$grid}
              isLoading={isFetching}
              countPerPage={PAGE_SIZE_ON_DELIVERY_AREAS}
              totalCount={data?.totalElements}
              totalPages={data?.totalPages}
              currentPage={data?.currentPage}
              rowData={data?.list || []}
              columnDefs={columnDefs}
              addButtonName="우편번호 추가"
              onChangePage={onChangePage}
              onExportData={handleExportData}
              onUploadData={handleUploadData}
              onAddRow={addModal.onOpenModal}
            />
          </HStack>
        </PageLayout>
      </FormProvider>

      {addModal.open && <DeliveryAreasAddModal open onRefresh={() => handleSubmit(queryKey)} onClose={addModal.onCloseModal} />}
      {exportModal.open && <ExcelDownloadModal open withPassword={false} serverAudit onConfirm={onExportData} onClose={exportModal.onCloseModal} />}
    </>
  )
}

export default DeliveryAreas
